import React, { useState, useEffect, useRef } from 'react';
import GlobalFunction from './globalFunction';
import { Grid, Row, Col, Button, Glyphicon, DropdownButton, MenuItem, ListGroup, ListGroupItem, Panel, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import EventSystem from './EventSystem';
import MyAudioPlayer from './myAudioPlayer';
import _ from 'lodash';
import { RingLoader } from 'react-spinners';
import keyGen from 'random-key';
import Draggable from 'react-draggable';
import adapter from 'webrtc-adapter';
import RmToolTip from './rmToolTip';
import { Tile, Title, Body, LoggingIndicator } from './style/tile.style';
import './css/webcamModule.css';

const BORDER_COLOURS = {
    warning: '#eea236',
    primary: '#2e6da4',
    danger: '#d43f3a',
    success: '#4cae4c',
    default: '#ccc',
    info: '#1b6d85'
};

const PC_CONFIG = {
    iceServers: [{
        urls: 'turn:prod01.radio.management',
        username: 'obclock',
        credential: 'Milt0101'
    }]
};

const SDP_CONSTRAINTS = { offerToReceiveAudio: true, offerToReceiveVideo: true };

export default function WebcamControlModule({ parameters, onSendMessage, addLogEntry, enableModuleLogging, mode }) {
    const [moduleWidth, setModuleWidth] = useState(0);
    const [moduleHeight, setModuleHeight] = useState(0);
    const [validationError, setValidationError] = useState(null);
    const [name, setName] = useState('');
    const [myRegisteredName, setMyRegisteredName] = useState('');
    const [registeredNames, setRegisteredNames] = useState([]);
    const [peerToConnectWith, setPeerToConnectWith] = useState('');
    const [initiator, setInitiator] = useState(false);
    const [peerConnectionCreated, setPeerConnectionCreated] = useState(false);
    const [callStatus, setCallStatus] = useState('none');
    const [hideRemoteVideo, setHideRemoteVideo] = useState(true);
    const [localVideoWidth, setLocalVideoWidth] = useState(0);
    const [localVideoHeight, setLocalVideoHeight] = useState(0);
    const [cameraMuted, setCameraMuted] = useState(false);
    const [micMuted, setMicMuted] = useState(false);
    const [remoteCameraMuted, setRemoteCameraMuted] = useState(false);
    const [remoteAudioMuted, setRemoteAudioMuted] = useState(false);
    const [localMediaDevices, setLocalMediaDevices] = useState([]);
    const [componentMounted, setComponentMounted] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState({ audiooutput: '', audioinput: '', videoinput: '' });
    const [fileManager, setFileManager] = useState(false);
    const [gracefulCallTermination, setGracefulCallTermination] = useState(false);
    const [initiatedHangUp, setInitiatedHangUp] = useState(false);
    const [terminationMessage, setTerminationMessage] = useState('');
    const [fileSelected, setFileSelected] = useState('');
    const [preRecordVideoFileName, setPreRecordVideoFileName] = useState('');
    const [audioListItems, setAudioListItems] = useState([]);
    const [localAudioHeight, setLocalAudioHeight] = useState(0);
    const [browserSupportFails, setBrowserSupportFails] = useState(false);
    const [captureStream, setCaptureStream] = useState(true);
    const [currentPlayingFromPlaylist, setCurrentPlayingFromPlaylist] = useState(null);
    const [expectingStop, setExpectingStop] = useState(false);
    const [playlistErrorMessage, setPlaylistErrorMessage] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [sendingLocalVideo, setSendingLocalVideo] = useState(true);
    const [sendingLocalAudio, setSendingLocalAudio] = useState(true);
    const [receivingRemoteVideo, setReceivingRemoteVideo] = useState(true);
    const [receivingRemoteAudio, setReceivingRemoteAudio] = useState(true);
    const [remoteConnectionSuccessful, setRemoteConnectionSuccessful] = useState(false);
    const [gettingLocalStream, setGettingLocalStream] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [negotiating, setNegotiating] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [headingFontSize, setHeadingFontSize] = useState(5);
    const [dataRow, setDataRow] = useState({ rowHeight: 10 });
    const [controlledPosition, setControlledPosition] = useState({ x: 0, y: 0 });
    const [loggingModule, setLoggingModule] = useState(false);
    const [highlight, setHighlight] = useState(false);
    const [parentCallStatus, setParentCallStatus] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [containerHeight, setContainerHeight] = useState(0);
    const [panelBodyContainerHeight, setPanelBodyContainerHeight] = useState(0);
    const [remoteAspectRatio, setRemoteAspectRatio] = useState(0);
    const [nonVideoRowHeight, setNonVideoRowHeight] = useState(0);

    // Mutable instance vars (not state)
    const localStreamRef = useRef({});
    const remoteStreamRef = useRef({});
    const pcRef = useRef({});
    const intervalIDRef = useRef(undefined);
    const timerIDRef = useRef(undefined);

    // DOM refs
    const localAudio = useRef(null);
    const localVideo = useRef(null);
    const remoteVideo = useRef(null);
    const fileManagerHeadings = useRef(null);
    const preRecordVideo = useRef(null);
    const audioLevelsCanvas = useRef(null);
    const videoLevelsCanvas = useRef(null);
    const audioFile = useRef(null);
    const container = useRef(null);
    const fontSizeChecker = useRef(null);
    const panelBodyContainer = useRef(null);
    const nonVideoRows = useRef(null);

    // Live ref for props/state used in callbacks
    const liveRef = useRef({ onSendMessage, addLogEntry, parameters });
    liveRef.current.onSendMessage = onSendMessage;
    liveRef.current.addLogEntry = addLogEntry;
    liveRef.current.parameters = parameters;

    // State ref - synced each render for use in EventSystem/WebRTC callbacks
    const sr = useRef({});
    sr.current = {
        callStatus, myRegisteredName, registeredNames, peerToConnectWith,
        peerConnectionCreated, initiator, sendingLocalVideo, sendingLocalAudio,
        remoteConnectionSuccessful, negotiating, cameraMuted, micMuted,
        initiatedHangUp, gracefulCallTermination, parentCallStatus,
        componentMounted, containerWidth, containerHeight, panelBodyContainerHeight,
        remoteAspectRatio, expectingStop, currentPlayingFromPlaylist,
    };

    useEffect(() => {
        if (mode === 'setup') {
            liveRef.current.addLogEntry('webcam in setupmode');
            return;
        }

        const token = keyGen.generate();
        const pcs = { action: 'webcamCallStatusUpdate', instanceToken: token, callStatus: 'none' };
        setParentCallStatus(pcs);

        const delay = Math.random() * 1000;
        timerIDRef.current = setTimeout(() => {
            const sendPcs = { ...pcs, callStatus: 'setup' };
            liveRef.current.onSendMessage(sendPcs);
            liveRef.current.addLogEntry('sending Setup');
        }, delay);

        enableModuleLogging();

        getModuleSize(
            parameters.containerHeight.container,
            parameters.config.w,
            parameters.containerHeight.maxCols
        );

        EventSystem.subscribe('WebcamControlModule', processMessageStable.current);
        EventSystem.subscribe('logStatus' + parameters.config.event_screen_module_id, logStatusChangeStable.current);
        EventSystem.subscribe('highlightModule' + parameters.config.event_screen_module_id, highlightStatusStable.current);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'register';
        liveRef.current.onSendMessage(msgObj);

        const maxH = panelBodyContainer.current.clientHeight - nonVideoRows.current.clientHeight;
        const storedName = JSON.parse(localStorage.getItem(
            parameters.config.module_name + '_' + parameters.config.event_screen_module_id
        )) ?? '';

        setNonVideoRowHeight(nonVideoRows.current.clientHeight);
        setContainerWidth(container.current.clientWidth);
        setContainerHeight(container.current.clientHeight);
        setName(storedName);
        setPanelBodyContainerHeight(panelBodyContainer.current.clientHeight);

        if (parameters.config.other_config.callType === 'audio') {
            try {
                localAudio.current.captureStream();
            } catch (err) {
                setCaptureStream(false);
            }
        }

        let constraints = {};
        if (parameters.config.other_config.callType === 'videoAudio') {
            constraints = { audio: true, video: true };
        } else if (parameters.config.other_config.callType === 'videoOnly') {
            constraints = { audio: false, video: true };
        } else {
            constraints = { audio: true, video: false };
            setLocalAudioHeight(localAudio.current.offsetHeight);
        }

        timerIDRef.current = setTimeout(() => {
            navigator.mediaDevices.getUserMedia(constraints)
                .then(stream => {
                    localStreamRef.current = stream;
                    getMediaDevice();
                    stopLocalDevice();
                })
                .catch(e => {
                    liveRef.current.addLogEntry('error: ' + JSON.stringify(e));
                });

            navigator.mediaDevices.ondevicechange = () => { getMediaDevice(); };

            const endPcs = { ...pcs, callStatus: 'endSetup' };
            liveRef.current.addLogEntry('endSetup #1');
            liveRef.current.onSendMessage(endPcs);
        }, delay);

        updateDimensions();

        return () => {
            EventSystem.unsubscribe('WebcamControlModule', processMessageStable.current);
            EventSystem.unsubscribe('logStatus' + parameters.config.event_screen_module_id, logStatusChangeStable.current);
            EventSystem.unsubscribe('highlightModule' + parameters.config.event_screen_module_id, highlightStatusStable.current);
            if (sr.current.callStatus === 'active') {
                const msg = getMessageObjectProForma();
                msg.action = 'bye';
                if (sr.current.initiator) {
                    msg.callInitiator = sr.current.myRegisteredName;
                    msg.callReceiver = sr.current.peerToConnectWith;
                } else {
                    msg.callReceiver = sr.current.myRegisteredName;
                    msg.callInitiator = sr.current.peerToConnectWith;
                }
                liveRef.current.onSendMessage(msg);
            }
            if (intervalIDRef.current !== undefined) {
                clearInterval(intervalIDRef.current);
            }
        };
    }, []);

    // CWRP: container size change
    const firstSizeRender = useRef(true);
    useEffect(() => {
        if (firstSizeRender.current) { firstSizeRender.current = false; return; }
        getModuleSize(
            parameters.containerHeight.container,
            parameters.config.w,
            parameters.containerHeight.maxCols
        );
    }, [parameters.containerHeight.container, parameters.config.w]);

    const logStatusChangeStable = useRef(function(data) { setLoggingModule(data); });
    const highlightStatusStable = useRef(function(status) { setHighlight(status); });
    const processMessageStable = useRef(function(messageObject) { processMessageImpl(messageObject); });

    function processMessageImpl(messageObject) {
        if (liveRef.current.parameters.config.event_grouping_seq !== messageObject.event_grouping_seq) return;

        const s = sr.current;

        if (messageObject.action === 'register') {
            setRegisteredNames([]);
            if (s.myRegisteredName.length > 0) {
                messageObject.action = 'instanceRegisteredName';
                messageObject.registeredName = s.myRegisteredName;
                messageObject.containerHeight = container.current.clientHeight;
                messageObject.containerWidth = container.current.clientWidth;
                messageObject.callStatus = s.callStatus;
                liveRef.current.onSendMessage(messageObject);
            }
        } else if (messageObject.action === 'callingResponse' &&
                   messageObject.callInitiator === s.myRegisteredName) {
            if (messageObject.response === 'busy') {
                setCallStatus('none');
                const pcs = { ...s.parentCallStatus, callStatus: 'none' };
                setParentCallStatus(pcs);
                liveRef.current.onSendMessage(pcs);
                setTerminationMessage('Busy');
                timerIDRef.current = setTimeout(() => setTerminationMessage(''), 4000);
            } else {
                startWebRTCSignalling();
            }
        } else if (messageObject.action === 'callingRequest' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            messageObject.action = 'callingResponse';
            if (s.callStatus === 'none') {
                messageObject.response = 'ok';
                setCallStatus('beingCalled');
                const pcs = { ...s.parentCallStatus, callStatus: 'beingCalled' };
                setParentCallStatus(pcs);
                liveRef.current.onSendMessage(pcs);
            } else {
                messageObject.response = 'busy';
            }
            liveRef.current.onSendMessage(messageObject);
        } else if (messageObject.action === 'instanceRegisteredName') {
            if (messageObject.registeredName !== s.myRegisteredName && s.callStatus === 'none') {
                const nameStructure = {
                    registeredName: messageObject.registeredName,
                    containerHeight: messageObject.containerHeight,
                    containerWidth: messageObject.containerWidth,
                    callStatus: messageObject.callStatus
                };
                setRegisteredNames(prev => {
                    const updated = [...prev, nameStructure];
                    if (updated.length === 1) {
                        setPeerToConnectWith(updated[0].registeredName);
                        setRemoteAspectRatio(updated[0].containerWidth / updated[0].containerHeight);
                    } else {
                        setPeerToConnectWith('');
                        setRemoteAspectRatio(0);
                    }
                    return updated;
                });
            }
        } else if (messageObject.action === 'bye' && s.peerConnectionCreated) {
            setGracefulCallTermination(true);
            if (messageObject.callReceiver === s.myRegisteredName) {
                handleRemoteHangup();
            } else if (messageObject.callInitiator === s.myRegisteredName) {
                handleRemoteHangup();
                messageObject.action = 'register';
                liveRef.current.onSendMessage(messageObject);
            }
        } else if (messageObject.action === 'cameraMute' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            setRemoteCameraMuted(messageObject.cameraMuted);
        } else if (messageObject.action === 'audioMute' &&
                   messageObject.callReceiver === s.myRegisteredName) {
            setRemoteAudioMuted(messageObject.micMuted);
        } else if (messageObject.action === 'remoteConnectionSuccess') {
            if (messageObject.callInitiator === s.myRegisteredName ||
                messageObject.callInitiator === s.peerToConnectWith) {
                setRemoteConnectionSuccessful(true);
                if (!s.sendingLocalVideo && !s.sendingLocalAudio) {
                    connectionSuccessful();
                } else if (remoteStreamRef.current !== null && s.callStatus !== 'active') {
                    connectionSuccessful();
                }
            }
        } else if (messageObject.action === 'webrtc') {
            liveRef.current.addLogEntry('webrtc message received' + JSON.stringify(messageObject.webrtc));
            if (messageObject.webrtc.type === 'offer' &&
                messageObject.callReceiver === s.myRegisteredName) {
                if (!s.initiator && !s.peerConnectionCreated) {
                    createPeerConnection();
                    setPeerConnectionCreated(true);
                    if (s.sendingLocalVideo) {
                        pcRef.current.addTrack(localStreamRef.current.getVideoTracks()[0], localStreamRef.current);
                    }
                    if (s.sendingLocalAudio) {
                        pcRef.current.addTrack(localStreamRef.current.getAudioTracks()[0], localStreamRef.current);
                    }
                    setPeerToConnectWith(messageObject.callInitiator);
                    setRemoteAspectRatio(messageObject.containerWidth / messageObject.containerHeight);
                }
                pcRef.current.setRemoteDescription(new RTCSessionDescription(messageObject.webrtc));
                pcRef.current.createAnswer()
                    .then(setLocalAndSendMessage, onCreateSessionDescriptionError);
                setReceivingRemoteVideo(messageObject.sendingVideo);
                setReceivingRemoteAudio(messageObject.sendingAudio);
            } else if (messageObject.webrtc.type === 'answer' &&
                       s.peerConnectionCreated &&
                       messageObject.callInitiator === s.myRegisteredName) {
                setPeerToConnectWith(messageObject.callReceiver);
                setReceivingRemoteAudio(messageObject.sendingAudio);
                setReceivingRemoteVideo(messageObject.sendingVideo);
                pcRef.current.setRemoteDescription(new RTCSessionDescription(messageObject.webrtc));
            } else if (messageObject.webrtc.type === 'candidate' &&
                       s.peerConnectionCreated &&
                       messageObject.callReceiver === s.myRegisteredName) {
                const candidate = new RTCIceCandidate({
                    sdpMLineIndex: messageObject.webrtc.label,
                    candidate: messageObject.webrtc.candidate
                });
                pcRef.current.addIceCandidate(candidate);
            }
        }
    }

    function getMessageObjectProForma() {
        return {
            sourceModuleName: liveRef.current.parameters.config.module_name,
            event_grouping_seq: liveRef.current.parameters.config.event_grouping_seq,
            customerID: liveRef.current.parameters.userProfile.customer_id,
            siteID: liveRef.current.parameters.userProfile.site_id,
            eventID: liveRef.current.parameters.config.event_id,
        };
    }

    function getModuleSize(height, columns, maxCols) {
        const width = Math.floor(GlobalFunction.getModuleWidth(columns, maxCols));
        setModuleWidth(width);
        setModuleHeight(height);
    }

    function updateDimensions() {
        const headingWidth = container.current.clientWidth - 80;
        const headingHeight = 30;
        let fontSize = GlobalFunction.getFontSize(
            liveRef.current.parameters.config.description,
            headingWidth, headingHeight,
            fontSizeChecker.current
        );
        if (fontSize > 22) fontSize = 22;
        setHeadingFontSize(fontSize);

        let rowHeight = Math.floor((liveRef.current.parameters.containerHeight.container - 55) / 2);
        if (rowHeight > 34) rowHeight = 34;
        setDataRow({ rowHeight });
    }

    function getMediaDevice() {
        liveRef.current.addLogEntry('In getMediaDevice', true);
        navigator.mediaDevices.enumerateDevices()
            .then(gotDevices)
            .catch(e => {
                liveRef.current.addLogEntry('Error enumerateDevices: ' + JSON.stringify(e));
            });
    }

    function deviceStorageKey() {
        return parameters.config.module_name + '_devices_' + parameters.config.event_screen_module_id;
    }

    function saveDeviceSelection(sel, deviceList) {
        const deviceIds = {};
        for (const kind of ['audiooutput', 'audioinput', 'videoinput']) {
            const dev = sel[kind] ? deviceList.find(d => d.kind === kind && d.label === sel[kind]) : null;
            deviceIds[kind] = dev ? dev.deviceId : '';
        }
        localStorage.setItem(deviceStorageKey(), JSON.stringify({ ...sel, deviceIds }));
    }

    function gotDevices(deviceInfo) {
        const sel = { audiooutput: '', audioinput: '', videoinput: '' };
        const devices = [];
        let lastKind = '';

        for (let x = 0; x < deviceInfo.length; x++) {
            if (liveRef.current.parameters.config.other_config.callType === 'videoOnly' &&
                deviceInfo[x].kind !== 'videoinput') continue;

            if (deviceInfo[x].deviceId !== 'default') {
                const newDevice = {
                    deviceId: deviceInfo[x].deviceId,
                    groupId: deviceInfo[x].groupId,
                    kind: deviceInfo[x].kind,
                    label: deviceInfo[x].label.length > 0 ? deviceInfo[x].label : 'Device ' + x,
                    default: deviceInfo[x].kind !== lastKind
                };
                if (deviceInfo[x].kind !== lastKind) {
                    lastKind = deviceInfo[x].kind;
                    sel[deviceInfo[x].kind] = newDevice.label;
                }
                devices.push(newDevice);
            }
        }

        devices.push({ kind: 'videoinput', label: 'None' });
        devices.push({ kind: 'audioinput', label: 'None' });

        if (liveRef.current.parameters.config.other_config.callType === 'audio') {
            sel.videoinput = '';
        }

        const stored = JSON.parse(localStorage.getItem(deviceStorageKey()));
        if (stored) {
            const storedIds = stored.deviceIds || {};
            for (const kind of ['audioinput', 'audiooutput', 'videoinput']) {
                const storedId = storedIds[kind];
                if (storedId) {
                    const match = devices.find(d => d.kind === kind && d.deviceId === storedId);
                    if (match) { sel[kind] = match.label; continue; }
                }
                const storedLabel = stored[kind];
                if (storedLabel && devices.find(d => d.kind === kind && d.label === storedLabel)) {
                    sel[kind] = storedLabel;
                }
            }
        }

        if (sel.videoinput === '') setSendingLocalVideo(false);
        if (sel.audioinput === '' || liveRef.current.parameters.config.other_config.callType === 'videoOnly') {
            setSendingLocalAudio(false);
        }

        setSelectedDevices(sel);
        setLocalMediaDevices(devices);
        setComponentMounted(true);
    }

    function getLocalStream() {
        const callType = liveRef.current.parameters.config.other_config.callType;
        const localDevice = callType === 'audio' ? localAudio.current : localVideo.current;

        const devs = sr.current;
        let audioConstraint = false;
        let videoConstraint = false;

        let idx = localMediaDevices.findIndex(d => d.kind === 'audioinput' && d.label === devs.selectedDevices?.audioinput);
        // use current state from sr since this may be called after state updates
        const currentSelectedDevices = sr.current.selectedDevices ?? selectedDevices;

        idx = localMediaDevices.findIndex(d => d.kind === 'audioinput' && d.label === currentSelectedDevices.audioinput);
        if (idx !== -1) audioConstraint = { deviceId: { exact: localMediaDevices[idx].deviceId } };

        idx = localMediaDevices.findIndex(d => d.kind === 'videoinput' && d.label === currentSelectedDevices.videoinput);
        if (idx !== -1 && callType !== 'audio') {
            videoConstraint = { deviceId: { exact: localMediaDevices[idx].deviceId } };
        }

        idx = localMediaDevices.findIndex(d => d.kind === 'audiooutput' && d.label === currentSelectedDevices.audiooutput);
        if (idx !== -1) {
            localDevice.setSinkId(localMediaDevices[idx].deviceId).catch(e => {
                liveRef.current.addLogEntry('Error attaching to SinkId: ' + JSON.stringify(e));
            });
        }

        if (!audioConstraint && !videoConstraint) {
            setGettingLocalStream(false);
            return;
        }

        const constraints = callType === 'audio'
            ? { audio: audioConstraint, video: false }
            : { audio: audioConstraint, video: videoConstraint };

        navigator.mediaDevices.getUserMedia(constraints)
            .then(stream => {
                localStreamRef.current = stream;
                if (callType !== 'audio') localDevice.srcObject = stream;
                setGettingLocalStream(false);
                try {
                    if (callType !== 'videoOnly') monitorAudioLevels();
                } catch (err) {
                    setGettingLocalStream(false);
                }
            })
            .catch(e => {
                liveRef.current.addLogEntry('getUserMedia() error: ' + JSON.stringify(e.name) + JSON.stringify(constraints));
                setGettingLocalStream(false);
            });
    }

    function monitorAudioLevels() {
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(localStreamRef.current);
        const scriptNode = audioContext.createScriptProcessor(2048, 1, 1);
        analyser.smoothingTimeConstant = 0.3;
        analyser.fftSize = 1024;
        microphone.connect(analyser);
        analyser.connect(scriptNode);
        scriptNode.connect(audioContext.destination);

        const callType = liveRef.current.parameters.config.other_config.callType;
        const canvasCtx = callType === 'audio'
            ? audioLevelsCanvas.current.getContext('2d')
            : videoLevelsCanvas.current.getContext('2d');

        scriptNode.onaudioprocess = function() {
            const array = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(array);
            let values = 0;
            for (let i = 0; i < array.length; i++) values += array[i];
            let average = (values / array.length) * 0.7;
            if (average > 30) average = 30;
            canvasCtx.clearRect(0, 0, 25, 30);
            canvasCtx.fillStyle = '#00ff00';
            canvasCtx.fillRect(0, 30 - average, 25, 30);
        };
    }

    function stopLocalDevice() {
        if (sr.current.sendingLocalVideo || sr.current.sendingLocalAudio) {
            const tracks = localStreamRef.current.getTracks?.();
            if (tracks) tracks.forEach(t => t.stop());
        }
        localStreamRef.current = null;
        const callType = liveRef.current.parameters.config.other_config.callType;
        const localDevice = callType === 'audio' ? localAudio.current : localVideo.current;
        if (localDevice) localDevice.srcObject = null;
    }

    function buttonClick(e) {
        const msgObj = getMessageObjectProForma();
        const s = sr.current;

        if (e.target.name === 'register') {
            if (s.myRegisteredName.length === 0) {
                setMyRegisteredName(s.name);
                const hasvideo = selectedDevices.videoinput !== '';
                const hasaudio = selectedDevices.audioinput !== '';
                setSendingLocalVideo(hasvideo);
                setSendingLocalAudio(hasaudio);
                setGettingLocalStream(true);
                getLocalStream();
            } else {
                setMyRegisteredName('');
                stopLocalDevice();
            }
            msgObj.action = 'register';
            liveRef.current.onSendMessage(msgObj);
        } else if (e.target.name === 'connect') {
            if (s.callStatus === 'none') {
                setInitiator(true);
                msgObj.callInitiator = s.myRegisteredName;
                msgObj.callReceiver = s.peerToConnectWith;
                msgObj.action = 'callingRequest';
                setCallStatus('calling');
                const pcs = { ...s.parentCallStatus, callStatus: 'calling' };
                setParentCallStatus(pcs);
                liveRef.current.onSendMessage(pcs);
            } else {
                msgObj.action = 'bye';
                if (s.initiator) {
                    msgObj.callInitiator = s.myRegisteredName;
                    msgObj.callReceiver = s.peerToConnectWith;
                } else {
                    msgObj.callReceiver = s.myRegisteredName;
                    msgObj.callInitiator = s.peerToConnectWith;
                }
            }
            liveRef.current.onSendMessage(msgObj);
        } else if (e.target.name === 'audioFile') {
            setFileManager(true);
        } else if (e.target.name === 'exitFileManager') {
            setFileManager(false);
        }
    }

    function hangupCall() {
        const s = sr.current;
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'bye';
        if (s.initiator) {
            msgObj.callInitiator = s.myRegisteredName;
            msgObj.callReceiver = s.peerToConnectWith;
        } else {
            msgObj.callReceiver = s.myRegisteredName;
            msgObj.callInitiator = s.peerToConnectWith;
        }
        liveRef.current.onSendMessage(msgObj);
        setInitiatedHangUp(true);
    }

    function startWebRTCSignalling() {
        const s = sr.current;
        if (!s.peerConnectionCreated) {
            createPeerConnection();
            setPeerConnectionCreated(true);
            if (s.sendingLocalVideo || s.sendingLocalAudio) {
                localStreamRef.current.getTracks().forEach(track => {
                    pcRef.current.addTrack(track, localStreamRef.current);
                });
            }
        }
    }

    function createPeerConnection() {
        try {
            pcRef.current = new RTCPeerConnection(PC_CONFIG);
            pcRef.current.onicecandidate = handleIceCandidate;
            pcRef.current.ontrack = handleRemoteTrackAdded;
            pcRef.current.onremovestream = handleRemoteStreamRemoved;
            pcRef.current.oniceconnectionstatechange = handleIceConnectionStateChange;
            pcRef.current.onnegotiationneeded = handleNegotiationNeeded;
            pcRef.current.onsignalingstatechange = handleSignallingStateChange;
        } catch (e) {
            liveRef.current.addLogEntry('Failed to create PeerConnection: ' + JSON.stringify(e.message));
        }
    }

    function handleIceCandidate(event) {
        if (event.candidate) {
            const s = sr.current;
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'webrtc';
            msgObj.webrtc = {
                type: 'candidate',
                label: event.candidate.sdpMLineIndex,
                id: event.candidate.sdpMid,
                candidate: event.candidate.candidate
            };
            msgObj.callInitiator = s.myRegisteredName;
            msgObj.callReceiver = s.peerToConnectWith;
            liveRef.current.onSendMessage(msgObj);
        }
    }

    function handleRemoteTrackAdded(event) {
        const s = sr.current;
        const callType = liveRef.current.parameters.config.other_config.callType;
        const remoteDevice = callType === 'audio' ? localAudio.current : remoteVideo.current;

        if (remoteDevice.srcObject === event.streams[0]) return;
        remoteStreamRef.current = event.streams[0];
        remoteDevice.srcObject = remoteStreamRef.current;

        const msgObj = getMessageObjectProForma();
        if (s.initiator) {
            msgObj.callInitiator = s.myRegisteredName;
            msgObj.callReceiver = s.peerToConnectWith;
        } else {
            msgObj.callInitiator = s.peerToConnectWith;
            msgObj.callReceiver = s.myRegisteredName;
        }
        msgObj.action = 'remoteConnectionSuccess';
        liveRef.current.onSendMessage(msgObj);

        if (s.remoteConnectionSuccessful && s.callStatus !== 'active') {
            connectionSuccessful();
        }
    }

    function connectionSuccessful() {
        const s = sr.current;
        if (s.callStatus === 'active') {
            alert('we have a double dipping on active connection');
            return;
        }
        setGracefulCallTermination(false);
        setHideRemoteVideo(false);
        setCallStatus('active');

        const pcs = { ...s.parentCallStatus, callStatus: 'active' };
        setParentCallStatus(pcs);
        liveRef.current.onSendMessage(pcs);

        if (s.initiator) {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'register';
            liveRef.current.onSendMessage(msgObj);
        }
    }

    function handleRemoteStreamRemoved() {
        handleRemoteHangup();
        const s = sr.current;
        if (s.initiator) {
            const msgObj = getMessageObjectProForma();
            msgObj.action = 'register';
            liveRef.current.onSendMessage(msgObj);
        }
    }

    function handleIceConnectionStateChange() {
        if (pcRef.current === null) return;
        if (pcRef.current.iceConnectionState === 'disconnected' && !sr.current.gracefulCallTermination) {
            pcRef.current.createOffer(
                setLocalAndSendMessage,
                handleCreateOfferError,
                { iceRestart: true, offerToReceiveVideo: true, offerToReceiveAudio: true }
            );
        }
    }

    function handleSignallingStateChange() {
        if (sr.current.initiator) {
            setNegotiating(pcRef.current.signalingState !== 'stable');
        }
    }

    function handleNegotiationNeeded() {
        const s = sr.current;
        if (s.initiator) {
            if (s.negotiating) return;
            setNegotiating(true);
            pcRef.current.createOffer(
                setLocalAndSendMessage,
                handleCreateOfferError,
                { offerToReceiveVideo: true, offerToReceiveAudio: true }
            );
        }
    }

    function handleCreateOfferError(event) {
        liveRef.current.addLogEntry('createOffer() error: ' + JSON.stringify(event));
        handleRemoteHangup();
    }

    function onCreateSessionDescriptionError(error) {
        liveRef.current.addLogEntry('Failed to create session description: ' + error.toString());
        handleRemoteHangup();
    }

    function handleRemoteHangup() {
        if (typeof pcRef.current !== 'undefined') {
            stopTheCall();
        }
    }

    function stopTheCall() {
        pcRef.current.close();
        pcRef.current = null;
        setCallStatus('none');

        const s = sr.current;
        const pcs = { ...s.parentCallStatus, callStatus: 'none' };
        setParentCallStatus(pcs);
        liveRef.current.onSendMessage(pcs);

        if (intervalIDRef.current !== undefined) clearInterval(intervalIDRef.current);

        if (s.cameraMuted) unMuteCamera();
        if (s.micMuted) unMuteMic();

        setIsActive(false);
        setInitiator(false);
        setPeerConnectionCreated(false);
        setCameraMuted(false);
        setMicMuted(false);
        setRemoteAudioMuted(false);
        setRemoteCameraMuted(false);
        setHideRemoteVideo(true);
        setControlledPosition({ x: 0, y: 0 });
        setRemoteConnectionSuccessful(false);

        if (!s.initiatedHangUp) {
            if (s.gracefulCallTermination) {
                setTerminationMessage('Remote end hung up');
            } else {
                setTerminationMessage('Connection broken');
            }
            timerIDRef.current = setTimeout(() => setTerminationMessage(''), 4000);
        }
        setInitiatedHangUp(false);
        setGracefulCallTermination(false);

        if (liveRef.current.parameters.config.other_config.callType !== 'video') {
            const availableWidth = panelBodyContainer.current.clientWidth;
            // original bug preserved: used this.setState.remoteAspectRatio (undefined)
            const imageDimensions = calcImageDimensions(availableWidth, s.maxInitLocalVideoHeight, undefined);
            setLocalVideoWidth(imageDimensions.screenWidth + 'px');
            setLocalVideoHeight(imageDimensions.screenHeight + 'px');
        }
    }

    function setLocalAndSendMessage(sessionDescription) {
        const params = liveRef.current.parameters;
        if (typeof params.config.other_config.audio_codec !== 'undefined') {
            const newSDP = setPreferredCodec(sessionDescription.sdp, 'audio', params.config.other_config.audio_codec);
            sessionDescription.sdp = newSDP;
        }
        pcRef.current.setLocalDescription(sessionDescription);

        const msgObj = getMessageObjectProForma();
        msgObj.action = 'webrtc';
        msgObj.webrtc = sessionDescription;
        const s = sr.current;
        if (sessionDescription.type === 'offer') {
            liveRef.current.addLogEntry('sending offer to other party' + JSON.stringify(msgObj.webrtc));
            msgObj.callInitiator = s.myRegisteredName;
            msgObj.callReceiver = s.peerToConnectWith;
            msgObj.containerHeight = container.current.clientHeight;
            msgObj.containerWidth = container.current.clientWidth;
        } else if (sessionDescription.type === 'answer') {
            msgObj.callInitiator = s.peerToConnectWith;
            msgObj.callReceiver = s.myRegisteredName;
        }
        msgObj.sendingVideo = s.sendingLocalVideo;
        msgObj.sendingAudio = s.sendingLocalAudio;
        liveRef.current.onSendMessage(msgObj);
    }

    function setPreferredCodec(sdp, type, codec) {
        const sdpLines = sdp.split('\r\n');
        const codecNumbers = [];
        let codecPreferenceIndex = 0;
        for (let i = 0; i < sdpLines.length; ++i) {
            if (sdpLines[i].indexOf('a=rtpmap') === 0) {
                if (sdpLines[i].toLowerCase().indexOf(codec.toLowerCase()) !== -1) {
                    codecNumbers.push(sdpLines[i].substring(9, sdpLines[i].indexOf(' ')));
                }
            } else if (sdpLines[i].indexOf('m=' + type) === 0) {
                codecPreferenceIndex = i;
            }
        }
        if (codecNumbers.length === 0) return sdp;
        const mLine = sdpLines[codecPreferenceIndex];
        const elements = mLine.split(' ');
        const newLine = elements.slice(0, 3);
        for (let i = 0; i < codecNumbers.length; ++i) newLine.push(codecNumbers[i]);
        for (let i = 3; i < elements.length; i++) {
            if (codecNumbers.indexOf(elements[i]) === -1) newLine.push(elements[i]);
        }
        sdpLines[codecPreferenceIndex] = newLine.join(' ');
        return sdpLines.join('\r\n');
    }

    function muteCam() {
        const newMuted = !cameraMuted;
        setCameraMuted(newMuted);
        for (let x = 0; x < localStreamRef.current.getVideoTracks().length; x++) {
            localStreamRef.current.getVideoTracks()[x].enabled = !newMuted;
        }
        const s = sr.current;
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'cameraMute';
        msgObj.callInitiator = s.myRegisteredName;
        msgObj.callReceiver = s.peerToConnectWith;
        msgObj.cameraMuted = newMuted;
        liveRef.current.onSendMessage(msgObj);
    }

    function unMuteCamera() {
        const tracks = localStreamRef.current.getVideoTracks();
        for (let x = 0; x < tracks.length; x++) tracks[x].enabled = true;
    }

    function muteMic() {
        const newMuted = !micMuted;
        setMicMuted(newMuted);
        for (let x = 0; x < localStreamRef.current.getAudioTracks().length; x++) {
            localStreamRef.current.getAudioTracks()[x].enabled = !newMuted;
        }
        const s = sr.current;
        const msgObj = getMessageObjectProForma();
        msgObj.action = 'audioMute';
        msgObj.callInitiator = s.myRegisteredName;
        msgObj.callReceiver = s.peerToConnectWith;
        msgObj.micMuted = newMuted;
        liveRef.current.onSendMessage(msgObj);
    }

    function unMuteMic() {
        const tracks = localStreamRef.current.getAudioTracks();
        for (let x = 0; x < tracks.length; x++) tracks[x].enabled = true;
    }

    function playerStatusChanged(newState, audioObject) {
        if (sr.current.callStatus === 'none') return;

        let newAudioTrack, newVideoTrack, newSourceTracks, newSourceStream, senders;

        if (newState === 'start') {
            const current = sr.current.currentPlayingFromPlaylist;
            if (current !== null) {
                setExpectingStop(true);
                current.pause();
                setCurrentPlayingFromPlaylist(audioObject);
            } else {
                setCurrentPlayingFromPlaylist(audioObject);
            }
            newSourceStream = audioObject.captureStream();
            newSourceTracks = newSourceStream.getTracks();
            for (let x = 0; x < newSourceTracks.length; x++) {
                if (newSourceTracks[x].kind === 'video') newVideoTrack = newSourceTracks[x];
                else newAudioTrack = newSourceTracks[x];
            }
            senders = pcRef.current.getSenders();
            for (let x = 0; x < senders.length; x++) {
                if (senders[x].track.kind === 'video') {
                    senders[x].replaceTrack(newVideoTrack);
                } else {
                    senders[x].replaceTrack(newAudioTrack)
                        .then(() => { senders = pcRef.current.getSenders(); })
                        .catch(err => { alert('replace failed %s', err); });
                }
            }
        } else if (newState === 'stop' && sr.current.expectingStop) {
            setExpectingStop(false);
        } else if (newState === 'stop') {
            newSourceTracks = localStreamRef.current.getTracks();
            for (let x = 0; x < newSourceTracks.length; x++) {
                if (newSourceTracks[x].kind === 'video') newVideoTrack = newSourceTracks[x];
                else newAudioTrack = newSourceTracks[x];
            }
            senders = pcRef.current.getSenders();
            for (let x = 0; x < senders.length; x++) {
                if (senders[x].track.kind === 'video') senders[x].replaceTrack(newVideoTrack);
                else senders[x].replaceTrack(newAudioTrack);
            }
            setCurrentPlayingFromPlaylist(null);
        }
    }

    function removePlayer(key) {
        const index = audioListItems.findIndex(item => item.key === key);
        if (index === -1) { alert('Cannot find %s in array to remove', key); return; }
        setAudioListItems(prev => prev.filter((_, i) => i !== index));
    }

    function selectAudioFile(e) {
        if (typeof e.target.files[0] !== 'undefined') {
            const file = e.target.files[0];
            const idx = audioListItems.findIndex(item => item.key === file.name);
            if (idx === -1) {
                setAudioListItems(prev => [...prev, (
                    <ListGroupItem key={file.name}>
                        <MyAudioPlayer sourceFile={file} onStatusChange={playerStatusChanged} onRemovePlayer={removePlayer} />
                    </ListGroupItem>
                )]);
            } else {
                setPlaylistErrorMessage('File with that name already loaded');
                timerIDRef.current = setTimeout(() => setPlaylistErrorMessage(''), 3000);
            }
        }
    }

    function handlePreRecordFileSelected(e) {
        const reader = new FileReader();
        const preRecordVideoEl = preRecordVideo.current;
        setPreRecordVideoFileName(e.target.files[0]);
        reader.onload = event => { preRecordVideoEl.src = event.target.result; };
        reader.readAsDataURL(e.target.files[0]);
    }

    function handleChange(e) {
        if (e.target.name === 'registerName') {
            const isDup = registeredNames.findIndex(r => r.registeredName === e.target.value) !== -1;
            setValidationError(isDup ? 'error' : null);
            setName(e.target.value);
            localStorage.setItem(
                parameters.config.module_name + '_' + parameters.config.event_screen_module_id,
                JSON.stringify(e.target.value)
            );
        }
    }

    function connectPeerSelected(data) {
        setPeerToConnectWith(data.target.value);
        const idx = registeredNames.findIndex(r => r.registeredName === data.target.value);
        setRemoteAspectRatio(registeredNames[idx].containerWidth / registeredNames[idx].containerHeight);
    }

    function selectVideoInput(eventKey) {
        setSelectedDevices(prev => {
            const next = { ...prev, videoinput: eventKey === 'None' ? '' : eventKey };
            saveDeviceSelection(next, localMediaDevices);
            return next;
        });
    }

    function selectAudioInput(eventKey) {
        setSelectedDevices(prev => {
            const next = { ...prev, audioinput: eventKey === 'None' ? '' : eventKey };
            saveDeviceSelection(next, localMediaDevices);
            return next;
        });
    }

    function selectAudioOutput(eventKey) {
        setSelectedDevices(prev => {
            const next = { ...prev, audiooutput: eventKey };
            saveDeviceSelection(next, localMediaDevices);
            return next;
        });
    }

    function calcImageDimensions(availableWidth, availableHeight, aspectRatio) {
        const neededWidth = Math.floor(availableHeight * aspectRatio);
        if (neededWidth < availableWidth) {
            return { screenWidth: neededWidth, screenHeight: availableHeight };
        }
        return { screenWidth: availableWidth, screenHeight: Math.floor(availableWidth / aspectRatio) };
    }

    function getDeviceMenuItem(kind, deviceInfo) {
        if (deviceInfo.kind === kind) {
            return <MenuItem key={deviceInfo.label} eventKey={deviceInfo.label}>{deviceInfo.label}</MenuItem>;
        }
        return null;
    }

    function createOptionItem(nameItem) {
        if (registeredNames.length > 0) {
            return (
                <option key={nameItem.registeredName} value={nameItem.registeredName} disabled={nameItem.callStatus === 'active'}>
                    {'Call: ' + nameItem.registeredName}
                </option>
            );
        }
        return null;
    }

    function handleStart() {
        if (callStatus !== 'active') return false;
        setDragging(true);
    }

    function handleStop() { setDragging(false); }

    function onControlledDrag(e, position) {
        setControlledPosition({ x: position.x, y: position.y });
    }

    // ─── Render calculations ───────────────────────────────────────────────────
    let localVideoHeightCalc = 0;
    let localVideoWidthCalc = 0;
    let localVideoTop = 0;
    let micTop;
    let micRight;

    let maxInitLocalVideoHeight = 0;
    if (componentMounted) {
        maxInitLocalVideoHeight =
            panelBodyContainer.current.clientHeight - nonVideoRows.current.clientHeight - 10;
        const callType = parameters.config.other_config.callType;
        if (callType !== 'video') {
            if (peerToConnectWith === '') {
                localVideoHeightCalc = maxInitLocalVideoHeight + 'px';
                localVideoWidthCalc = containerWidth - 10 + 'px';
                localVideoTop = containerHeight - maxInitLocalVideoHeight - 8 + 'px';
            } else {
                if (callStatus !== 'active') {
                    const dims = calcImageDimensions(containerWidth - 10, maxInitLocalVideoHeight, remoteAspectRatio);
                    localVideoWidthCalc = dims.screenWidth + 'px';
                    localVideoHeightCalc = dims.screenHeight + 'px';
                    localVideoTop = containerHeight - dims.screenHeight - 8 + 'px';
                } else {
                    let dims;
                    if (receivingRemoteVideo) {
                        dims = calcImageDimensions(containerWidth * 0.4, containerHeight * 0.4, remoteAspectRatio);
                    } else {
                        dims = calcImageDimensions(containerWidth, containerHeight, remoteAspectRatio);
                    }
                    localVideoWidthCalc = dims.screenWidth + 'px';
                    localVideoHeightCalc = dims.screenHeight + 'px';
                    localVideoTop = '0px';
                    if (sendingLocalVideo) {
                        if (localVideoWidthCalc < localVideoHeightCalc) { micTop = 7; micRight = 40; }
                        else { micTop = 38; micRight = 5; }
                    } else {
                        micTop = 7; micRight = 5;
                    }
                }
            }
        }
    }

    const defaultAudioOutput = selectedDevices.audiooutput;
    const defaultAudioInput = selectedDevices.audioinput === '' ? 'None' : selectedDevices.audioinput;
    const defaultVideoInput = selectedDevices.videoinput === '' ? 'None' : selectedDevices.videoinput;

    const callType = parameters.config.other_config.callType;

    let deviceToolTipText = (
        <span>Video Input: <strong>{defaultVideoInput}</strong><br />Audio Input: <strong>{defaultAudioInput}</strong><br />Audio Output: <strong>{defaultAudioOutput}</strong><br /><br /><strong>Click to change</strong></span>
    );
    if (callType === 'videoOnly') {
        deviceToolTipText = <span>Video Input: <strong>{defaultVideoInput}</strong><br /><br /><strong>Click to change</strong></span>;
    } else if (callType === 'audio') {
        deviceToolTipText = <span>Audio Input: <strong>{defaultAudioInput}</strong><br />Audio Output: <strong>{defaultAudioOutput}</strong><br /><br /><strong>Click to change</strong></span>;
    }

    let deviceSelectColumns = 4;
    if (containerWidth < 300) deviceSelectColumns = 12;
    else if (containerWidth < 500) deviceSelectColumns = 6;

    let fmListGroupHeight = 0;
    if (fileManager && fileManagerHeadings.current) {
        fmListGroupHeight = panelBodyContainerHeight - 40 - fileManagerHeadings.current.offsetHeight - 10;
    }

    const bodyHeight = parameters.containerHeight.container - 4 - 37 - 2;
    const rowStyle = { height: (parameters.containerHeight.container - 2) + 'px', borderStyle: 'none', boxShadow: '1px 2px 4px' + GlobalFunction.getPaletteColour(parameters.config.colour) + '63' };
    const remoteVideoStyle = {
        height: parameters.containerHeight.container - 7 + 'px',
        position: 'fixed', width: containerWidth - 7 + 'px',
        top: '4px', left: '3px', hidden: hideRemoteVideo,
        borderStyle: 'none', borderRadius: '1px'
    };
    const localVideoStyle = {
        height: localVideoHeightCalc, width: localVideoWidthCalc,
        objectPosition: 'top',
        cursor: callStatus === 'active' ? 'move' : 'default',
        zIndex: 1, borderStyle: sendingLocalVideo ? 'solid' : 'none'
    };
    const preRecordVideoStyle = {
        height: '100px', width: '100px', position: 'absolute',
        objectPosition: 'top', borderRadius: '0.1px', zIndex: 2,
        left: 0, top: localVideoTop, backgroundColor: 'red'
    };
    const disconectMessageStyle = {
        height: localVideoHeightCalc, width: '100%', position: 'absolute',
        objectPosition: 'top', borderRadius: '0.1px', zIndex: 2, left: 0,
        top: localVideoTop, textAlign: 'center', marginTop: '10%',
        display: terminationMessage === '' ? 'none' : 'block'
    };
    const webcamHeading = parameters.config.description.length > 0 ? parameters.config.description : 'Webcam';
    const borderColor = BORDER_COLOURS[parameters.config.colour];
    let ringLoaderSize = containerHeight;
    if (containerHeight > containerWidth) ringLoaderSize = containerWidth;
    const ringLeft = ((containerWidth - ringLoaderSize) / 2) + 'px';
    const ringTop = ((containerHeight - ringLoaderSize) / 2) + 'px';
    const audioFileInputId = 'audioFile' + parameters.config.event_grouping_seq;
    let audioBarWidth = 0;
    let connectFieldWidth = '80%';
    if (containerWidth > 350) connectFieldWidth = '40%';

    const nameFieldStyle = {
        marginLeft: '10px', height: dataRow.rowHeight + 'px', width: '100px',
        lineHeight: '14px', paddingTop: dataRow.rowHeight === 34 ? '3%' : 0,
        backgroundColor: parameters.colours.backgroundColor,
        color: validationError === 'error' ? 'red' : parameters.colours.color,
        borderStyle: 'none',
        borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66',
        flexGrow: 1000, flexShrink: 7
    };
    const registerButtonDisabled = (
        name.length === 0 || gettingLocalStream || callStatus === 'active' ||
        validationError === 'error' || !componentMounted
    );
    const devicesButtonDisabled = !componentMounted;
    const connectButtonDisabled = (
        peerToConnectWith.length === 0 || registeredNames.length === 0 ||
        myRegisteredName.length === 0 || callStatus !== 'none'
    );

    const options = [];
    if (registeredNames.length === 0) {
        options.push(<option key="noRegistration" value="default" disabled={false}>Waiting...</option>);
    } else if (registeredNames.length > 1) {
        options.push(<option key="choose" value="default" disabled={false}>Select...</option>);
    }
    _.map(registeredNames, nameItem => options.push(createOptionItem(nameItem)));

    return (
        <Tile height={parameters.containerHeight.container - 4 + 'px'} highlight={highlight} ref={container}>
            <Modal show={showModal} autoFocus>
                <Modal.Header>
                    <Modal.Title>Select Devices</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col xs={deviceSelectColumns} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px', display: 'block' }}>
                            <img src="staticImages/webcam-transparent.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2, display: callType === 'audio' ? 'none' : 'block' }} />
                            <DropdownButton title={defaultVideoInput} bsStyle="primary"
                                id={'videoinput' + parameters.config.event_grouping_seq}
                                name="videoinput" key="videoinput"
                                style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', display: callType === 'audio' ? 'none' : 'block' }}
                                onSelect={selectVideoInput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('videoinput', device))}
                            </DropdownButton>
                        </Col>
                        <Col xs={deviceSelectColumns} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px', display: callType === 'videoOnly' ? 'none' : 'block' }}>
                            <img src="staticImages/mic-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2, display: 'block' }} />
                            <DropdownButton title={defaultAudioInput} bsStyle="primary"
                                id={'connectWith' + parameters.config.event_grouping_seq}
                                name="audioinput" key="audioinput"
                                style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', display: 'block' }}
                                onSelect={selectAudioInput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('audioinput', device))}
                            </DropdownButton>
                        </Col>
                        <Col xs={deviceSelectColumns} style={{ whiteSpace: 'nowrap', paddingTop: '5px', paddingBottom: '5px', display: callType === 'videoOnly' ? 'none' : 'block' }}>
                            <img src="staticImages/speaker-on.svg" height="25" width="30"
                                style={{ float: 'left', position: 'absolute', top: 0, bottom: 0, left: '15px', margin: 'auto', zIndex: 2, display: defaultAudioOutput.length === 0 ? 'none' : 'block' }} />
                            <DropdownButton title={defaultAudioOutput} bsStyle="primary"
                                id={'audiooutput' + parameters.config.event_grouping_seq}
                                name="audiooutput" key="audiooutput"
                                style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', left: '0', paddingLeft: '30px', zIndex: 1, textAlign: 'left', display: defaultAudioOutput.length === 0 ? 'none' : 'block' }}
                                onSelect={selectAudioOutput}>
                                {_.map(localMediaDevices, device => getDeviceMenuItem('audiooutput', device))}
                            </DropdownButton>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setShowModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Title style={{ fontSize: headingFontSize + 'px' }}>
                <span style={{ paddingLeft: '5px' }}>{webcamHeading}</span>
                <RmToolTip itemText="devices" itemType="icon" placement="bottom"
                    style={{ position: 'absolute', top: '0', right: 0, height: '30px', width: '35px', paddingTop: '7px', fontSize: '22px', zIndex: 10 }}
                    disabled={devicesButtonDisabled} name="devices"
                    onClick={() => { if (!devicesButtonDisabled) setShowModal(true); }}
                    toolTipText={deviceToolTipText} />
                <RmToolTip itemText="playlist_play" itemType="icon" placement="bottom"
                    style={{ position: 'absolute', top: '0', right: '35px', height: '30px', width: '35px', display: callType === 'audio' ? 'block' : 'none', paddingTop: '3px', fontSize: '28px', zIndex: 10 }}
                    name="audioFile" onClick={buttonClick}
                    toolTipText={<span>Load and play<br />pre-recorded<br />audio clips</span>} />
            </Title>

            <Body style={{ height: bodyHeight + 'px' }} ref={panelBodyContainer}>
                <Grid fluid={false} className="react-grid-layout" style={{ height: '100%', width: '100%', paddingLeft: 0, paddingRight: 0 }}>
                    <div style={{ height: 0, width: 0, left: ringLeft, top: ringTop, position: 'absolute', zIndex: 100 }}>
                        <RingLoader color="#d41e3d" loading={callStatus !== 'none' && callStatus !== 'active'} size={300} />
                    </div>

                    {/* REGISTER & CONNECT */}
                    <div ref={nonVideoRows}
                        style={{ display: callStatus === 'active' ? 'none' : 'flex', flexWrap: 'wrap', position: 'absolute', top: 0, width: '100%', zIndex: 10 }}>
                        <div style={{ display: 'inline-flex', alignItems: 'flex-end', width: '120px', flexGrow: 2, flexShrink: 1 }}>
                            <input type="text" name="registerName" style={nameFieldStyle}
                                autoFocus={true} placeholder="Name"
                                value={name} onChange={handleChange} />
                            <RmToolTip itemText={myRegisteredName === '' ? 'perm_identity' : 'highlight_off'}
                                itemType="icon" placement="bottom"
                                style={{ display: 'inline-block', fontSize: '26px', lineHeight: '26px', verticalAlign: 'middle', height: '26px', flexGrow: 2, flexShrink: 1, textAlign: 'right', marginRight: '10px' }}
                                disabled={registerButtonDisabled} name="register"
                                onClick={registerButtonDisabled ? null : buttonClick}
                                toolTipText={<span>{myRegisteredName === '' ? 'Register' : 'Deregister'}</span>} />
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'flex-end', width: '120px', flexGrow: 2, flexShrink: 1 }}>
                            <select style={{ borderStyle: 'none', width: '80%', marginLeft: '10px', backgroundColor: parameters.colours.backgroundColor, height: dataRow.rowHeight + 'px', lineHeight: dataRow.rowHeight + 'px', flexGrow: 9, flexShrink: 7 }}
                                onChange={connectPeerSelected} required value={peerToConnectWith}>
                                {options}
                            </select>
                            <RmToolTip itemText={callStatus === 'active' ? 'call_end' : 'call'}
                                itemType="icon" placement="bottom"
                                style={{ display: 'inline-block', fontSize: '26px', lineHeight: '26px', marginRight: '10px', verticalAlign: 'middle', height: '26px', marginLeft: '5px', flexGrow: 2, flexShrink: 1, textAlign: 'right' }}
                                disabled={connectButtonDisabled} name="connect"
                                onClick={connectButtonDisabled ? null : buttonClick}
                                toolTipText={<span>Call</span>} />
                        </div>
                    </div>

                    {/* VIDEO CALL ACTIVE */}
                    <Row style={{ marginLeft: 0, marginRight: 0, display: callType === 'audio' ? 'none' : 'block' }}>
                        <div style={disconectMessageStyle}>
                            <span style={{ color: 'white', fontSize: '16pt', backgroundColor: 'gray', padding: '5px 10px', borderRadius: '20px' }}>{terminationMessage}</span>
                        </div>
                        <div style={{ height: parameters.containerHeight.container + 'px', position: 'fixed', width: '100%', top: 0, left: 0 }}>
                            <div style={{ height: parameters.containerHeight.container + 'px' }} hidden={hideRemoteVideo}>
                                <video ref={remoteVideo} autoPlay playsInline hidden={hideRemoteVideo} className="remoteVideo" style={remoteVideoStyle} />
                            </div>
                        </div>
                        <span style={{ color: 'black', backgroundColor: 'white', fontSize: '14px', lineHeight: '20px', opacity: 0.6, paddingLeft: '5pt', paddingRight: '5pt', borderRadius: '5pt', display: hideRemoteVideo ? 'none' : 'block', position: 'fixed', top: '10px', left: '10px' }}>
                            {peerToConnectWith}
                        </span>
                        <span style={{ color: 'white', fontSize: '15pt', display: remoteCameraMuted || (!receivingRemoteVideo && callStatus === 'active') ? 'block' : 'none', position: 'fixed', top: '35px', left: '10px' }}>
                            {remoteCameraMuted ? 'Camera Is Muted' : 'Not sending Video'}
                        </span>
                        <span style={{ color: 'white', fontSize: '15pt', display: remoteAudioMuted || (!receivingRemoteAudio && callStatus === 'active' && callType !== 'videoOnly') ? 'block' : 'none', position: 'fixed', top: '60px', left: '10px' }}>
                            {remoteAudioMuted ? 'Audio Is Muted' : 'Not sending Audio'}
                        </span>
                    </Row>

                    {/* AUDIO CALL ACTIVE */}
                    <Row style={{ marginLeft: 0, marginRight: 0, display: callType === 'audio' && callStatus === 'active' ? 'block' : 'none' }}>
                        <Col xs={12}>
                            <span style={{ color: 'white', fontSize: '14pt', whiteSpace: 'nowrap', paddingLeft: '20px' }}>{fileSelected}</span>
                            <br />
                            <audio autoPlay ref={localAudio} style={{ width: '100%', position: 'fixed', bottom: 0, left: 0 }} />
                            <div style={{ color: 'black', backgroundColor: 'white', width: audioBarWidth + 'px', height: '40px', borderRadius: '40px', borderStyle: 'solid', position: 'fixed', bottom: 0, left: 0, zIndex: 15, margin: '20px' }}
                                className={isActive ? 'onAir active' : 'onAir'} />
                            <img src={micMuted ? 'staticImages/hollow-mic-off.svg' : 'staticImages/hollow-mic-on.svg'} height="30" width="25" className="cog"
                                onClick={muteMic}
                                style={{ zIndex: 21, position: 'fixed', bottom: '25px', left: '30px', borderRadius: '40px', display: callStatus === 'active' && selectedDevices.audioinput !== '' ? 'inline-block' : 'none' }} />
                            <canvas ref={audioLevelsCanvas} width="15px" height="25px"
                                style={{ backgroundColor: 'white', zIndex: 19, position: 'fixed', bottom: '26px', left: '35px' }} />
                        </Col>
                    </Row>

                    {/* AUDIO CALL TERMINATION MESSAGE */}
                    <Row style={{ marginLeft: 0, marginRight: 0, position: 'fixed', bottom: 0, left: 0, zIndex: 15, width: '100%', textAlign: 'center', display: callType === 'audio' && terminationMessage.length > 0 ? 'block' : 'none' }}>
                        <span style={{ color: 'white', fontSize: '16pt', backgroundColor: 'gray', padding: '5px 10px', borderRadius: '20px' }}>{terminationMessage}</span>
                    </Row>

                    {/* VIDEO HANGUP BUTTON */}
                    <Row style={{ right: '5px', position: 'fixed', bottom: '5px', width: '100%', padding: '10px 0 0 10px', zIndex: 20 }}>
                        <Col xs={12} style={{ textAlign: 'right' }}>
                            <Glyphicon onClick={hangupCall} className="hangUp" name="hangup"
                                style={{ color: 'red', fontSize: '16pt', display: hideRemoteVideo ? 'none' : 'inline-block', background: callType === 'audio' ? 'none' : '#8c838361', margin: callType === 'audio' ? '15px' : 0 }}
                                bsSize="large" glyph="earphone" />
                        </Col>
                    </Row>

                    {/* AUDIO PLAYLIST */}
                    <Row style={{ display: callType === 'audio' && fileManager ? 'block' : 'none', zIndex: fileManager ? 10 : -10, position: 'absolute', top: 0, height: '100%', width: '100%', marginRight: '-10px', marginLeft: 0 }}
                        className="react-grid-layout">
                        <Col xs={12}>
                            <div ref={fileManagerHeadings}>
                                <Row>
                                    <Col xs={3} style={{ paddingTop: '5px' }}>
                                        <input type="file" id={audioFileInputId} name="audioFile"
                                            style={{ opacity: 0, position: 'absolute', pointerEvents: 'none', width: '1px', height: '1px' }}
                                            accept=".mp3" onChange={selectAudioFile} />
                                        <label htmlFor={audioFileInputId}>
                                            <span className="btn btn-warning">Add Audio</span>
                                        </label>
                                    </Col>
                                    <Col xs={7} style={{ paddingTop: '5px', color: 'red', fontSize: '16pt' }}>{playlistErrorMessage}</Col>
                                    <Col xs={2} style={{ paddingTop: '5px' }}>
                                        <Button bsStyle="danger" style={{}} name="exitFileManager" className="pull-right" onClick={buttonClick}>Exit</Button>
                                    </Col>
                                </Row>
                            </div>
                            <Row>
                                <Col xs={12}>
                                    <ListGroup style={{ height: fmListGroupHeight + 'px', width: '100%', overflowY: 'scroll', borderColor, borderWidth: '3px', borderStyle: audioListItems.length === 0 ? 'none' : 'solid', backgroundColor: audioListItems.length === 0 ? '' : 'white' }}>
                                        {audioListItems}
                                    </ListGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                    {/* BROWSER NOT SUPPORTED */}
                    <div style={{ display: browserSupportFails ? 'block' : 'none', height: parameters.containerHeight.container + 'px', width: '100%', zIndex: 1000, backgroundColor: 'blue', color: 'yellow', fontSize: '26pt', position: 'fixed', top: 0, left: 0, padding: '10px' }}>
                        Your browser does not support webrtc audio and video, this module disabled
                    </div>
                </Grid>
            </Body>

            <Draggable bounds="parent" position={controlledPosition} onStart={handleStart} onStop={handleStop} onDrag={onControlledDrag}>
                <div style={{ zIndex: 30, position: callStatus === 'active' ? 'fixed' : 'absolute', right: '0px', top: localVideoTop, margin: '5px 5px -3px 3px', display: callType === 'audio' ? 'none' : (myRegisteredName.length === 0 ? 'none' : 'block'), width: localVideoWidthCalc, height: localVideoHeightCalc }}>
                    <img src={cameraMuted ? 'staticImages/webcam-off.svg' : 'staticImages/webcam-on.svg'} height="25" width="25" className="cog"
                        onClick={muteCam}
                        style={{ zIndex: 3, position: 'fixed', top: '5px', right: '7px', borderRadius: '20px', display: hideRemoteVideo ? 'none' : (sendingLocalVideo ? 'inline-block' : 'none') }} />
                    <img src={micMuted ? 'staticImages/hollow-mic-off.svg' : 'staticImages/hollow-mic-on.svg'} height="25" width="25" className="cog"
                        onClick={muteMic}
                        style={{ zIndex: 3, position: 'fixed', top: micTop + 'px', right: micRight + 'px', borderRadius: '20px', display: hideRemoteVideo ? 'none' : (sendingLocalAudio ? 'inline-block' : 'none') }} />
                    <canvas ref={videoLevelsCanvas} width="11px" height="20px"
                        style={{ zIndex: 2, backgroundColor: 'white', position: 'fixed', top: (micTop + 2) + 'px', right: (micRight + 7) + 'px', display: hideRemoteVideo ? 'none' : (sendingLocalAudio ? 'inline-block' : 'none') }} />
                    <video ref={localVideo} autoPlay muted playsInline
                        hidden={myRegisteredName.length === 0}
                        className="localVideo box" style={localVideoStyle} />
                </div>
            </Draggable>

            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }} ref={fontSizeChecker} />
            <LoggingIndicator logStatus={loggingModule} />
        </Tile>
    );
}
